/**
 * A loggable summary of a failure from the loyalty client.
 *
 * Exists because the obvious thing to log is not enough. `joinFailureCode()` names only the
 * rejections the join ROUTE documents — six since `0.9.0`, which added `not_provisioned` and
 * `consent_unavailable` — and it returns null for everything else, so a log line built from it
 * alone reports "unknown" for every auth failure, every timeout and every 5xx, which are precisely
 * the cases someone is trying to diagnose. Only `LoyaltyHttpError` carries a code at
 * all; `LoyaltyAuthError` and `LoyaltyNetworkError` extend `LoyaltyError` directly, so they can
 * never produce one.
 *
 * Reads defensively rather than by `instanceof`: this runs in a host whose copy of the package is
 * the linked sibling checkout, and a class identity check that silently fails is exactly the wrong
 * thing to put in a diagnostic.
 *
 * @param {unknown} error Anything thrown by the client.
 * @return {Object} Plain object, safe to hand to console.warn.
 */
export function describeLoyaltyError( error ) {
	if ( ! error || typeof error !== 'object' ) {
		return { kind: typeof error, value: String( error ) };
	}

	const detail = { kind: error.name || 'Error', message: error.message };

	// LoyaltyAuthError and LoyaltyHttpError both carry one; a network error does not.
	if ( typeof error.status === 'number' ) {
		detail.status = error.status;
	}

	// LoyaltyAuthError. `authLayer` is the field that says whether the request even reached the
	// API — 'edge' means it did not — and `requiresReauth` says whether retrying could ever work.
	if ( error.authLayer !== undefined ) {
		detail.authLayer = error.authLayer;
		detail.requiresReauth = error.requiresReauth;
	}

	// LoyaltyHttpError. The server's own envelope, which is where an unrecognised error code hides.
	if ( error.body !== undefined ) {
		detail.body = error.body;
	}

	// Native cause: for a token provider that failed, this is the identity provider's own error and
	// the only diagnostic there is, because nothing was ever sent.
	if ( error.cause ) {
		detail.cause = error.cause.message || String( error.cause );
	}

	return detail;
}
